import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { toast } from "sonner";
import { Camera, FileDown, Trash2 } from "lucide-react";

export default function DiaryPage() {
  const { tripId } = useParams();
  const { API, authHeaders, token, user } = useAuth();
  const { trip } = useContext(TripCtx);
  const [entries, setEntries] = useState([]);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const isArchived = trip.archived;

  const load = () => axios.get(`${API}/trips/${tripId}/diary`, { headers: authHeaders }).then((r) => setEntries(r.data));
  useEffect(() => { load(); }, [tripId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/trips/${tripId}/diary`, { title: title || null, body }, { headers: authHeaders });
      setTitle(""); setBody("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (eid, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/diary/${eid}/photo`, fd,
        { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      load();
    } catch { toast.error("Photo upload failed"); }
  };

  const del = async (eid) => {
    if (!confirm("Delete this entry?")) return;
    await axios.delete(`${API}/diary/${eid}`, { headers: authHeaders });
    load();
  };

  return (
    <div data-testid="diary-page" className="py-4">
      <p className="tcm-eyebrow">{trip.destination}</p>
      <div className="flex justify-between items-end">
        <h1 className="font-display text-4xl mt-2">{isArchived ? "Memories" : "Diary"}</h1>
        <div className="flex gap-2">
          {entries.length > 0 && (
            <a data-testid="download-memories" href={`${API}/trips/${tripId}/memories.pdf?auth=${token}`}
               className="tcm-btn-outline inline-flex items-center gap-2">
              <FileDown size={12} /> PDF
            </a>
          )}
          {user?.role === "lead_client" && (
            <a data-testid="order-book" href={`/trips/${tripId}/memory-book`}
               className="tcm-btn-primary inline-flex items-center gap-2">
              <FileDown size={12} /> Order print
            </a>
          )}
        </div>
      </div>
      <hr className="tcm-gold-rule w-16 my-6" />

      {!isArchived && user?.role === "lead_client" && (
        <form onSubmit={submit} className="tcm-double-frame mb-8">
          <div className="tcm-inner p-5 space-y-3">
            <input data-testid="diary-title" className="tcm-input" placeholder="Title (optional)"
                   value={title} onChange={(e) => setTitle(e.target.value)} />
            <textarea data-testid="diary-body" className="tcm-input" rows="4" placeholder="What happened today?"
                      value={body} onChange={(e) => setBody(e.target.value)} required />
            <button data-testid="diary-submit" disabled={busy} className="tcm-btn-primary">Add entry</button>
          </div>
        </form>
      )}

      <div className="space-y-8">
        {entries.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)] py-6">No entries yet. Start capturing your trip.</p>}
        {entries.map((e) => (
          <div key={e.id} className="tcm-double-frame" data-testid={`diary-entry-${e.id}`}>
            <div className="tcm-inner p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="tcm-eyebrow">{e.created_at?.slice(0, 10)}{e.day ? ` · Day ${e.day}` : ""}</p>
                  {e.title && <p className="font-display text-2xl mt-1">{e.title}</p>}
                </div>
                {!isArchived && user?.role === "lead_client" && (
                  <button data-testid={`del-diary-${e.id}`} onClick={() => del(e.id)} className="tcm-eyebrow">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <p className="mt-3 whitespace-pre-wrap text-[color:var(--tcm-navy)]">{e.body}</p>
              {e.photos?.length > 0 && (
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {e.photos.map((p) => (
                    <img key={p.id} data-testid={`photo-${p.id}`} src={`${API}/diary/photo/${p.id}?auth=${token}`}
                         alt="" className="w-full h-40 object-cover border border-[#1E2D50]" />
                  ))}
                </div>
              )}
              {!isArchived && user?.role === "lead_client" && (
                <label className="mt-4 inline-flex items-center gap-2 tcm-btn-outline cursor-pointer">
                  <Camera size={12} /> Add photo
                  <input data-testid={`photo-input-${e.id}`} type="file" accept="image/*" className="hidden"
                         onChange={(ev) => uploadPhoto(e.id, ev.target.files[0])} />
                </label>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
